import { useEffect, useRef, useState } from "react";
import {
  Grid2 as Grid,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  FormControlLabel,
  Checkbox,
  CardContent,
  Card,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LoadingButton from "@mui/lab/LoadingButton";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import LockIcon from "@mui/icons-material/Lock";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckIcon from "@mui/icons-material/Check";
import { useDispatch, useSelector } from "react-redux";
import {
  deleteUserPackageForm,
  handleLoadDocument,
  lockDocument,
  unlockDocument,
  updateNewFileName,
  updateProcessOptions,
  updateSelectedPackageFormObject,
  updateSelectedPackageObject,
  updateTempFields,
} from "../../store/actions/userPackagesActions";
import { FaPlay } from "react-icons/fa";
import Magnifier from "../customComponents/Magnifier";
import RenderedPackageFormFields from "./RenderedPackageFormFields";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DeleteFormModal from "./modals/DeleteFormModal";
import { currentProcessingFileBase } from "../../data/userPackage/userPackageBase";
import { getSignersWithFields } from "./helpers/utility";
import { getLockValidationTodos } from "./helpers/formActionValidation";

const steps = ["Document Actions", "Record/Signer/Notary Information"];

const SelectedPackage = ({
  currentProcessingFile,
  setCurrentProcessingFile,
  setAddingNewPackage,
  setPackagePanelOpen,
  savedPackagesActive,
  currentFormsActive,
  activeStep,
  setActiveStep,
  setSavedPackagesActive,
  setCurrentFormsActive,
  loadDocumentLoading,
  setLoadDocumentLoading,
  setOpenProcessModal,
}) => {
  const dispatch = useDispatch();
  const scrollInputs = useRef(null);

  const { userPackages, selectedPackage, selectedPackageForm } = useSelector(
    (state) => state.userPackages
  );
  const [updatingFileName, setUpdatingFileName] = useState(null);
  const [newFileName, setNewFileName] = useState("");
  const [deleteFormModalOpen, setDeleteFormModalOpen] = useState(false);
  const [deleteFormLoading, setDeleteFormLoading] = useState(false);
  const [warnLock, setWarnLock] = useState(false);
  const [lockLoading, setLockLoading] = useState(false);
  const [lockErrorText, setLockErrorText] = useState("");
  const [showSigner1, setShowSigner1] = useState(false);
  const [showSigner2, setShowSigner2] = useState(false);
  const [showSigner3, setShowSigner3] = useState(false);
  const [options, setOptions] = useState({});

  const [lockChecklist, setLockChecklist] = useState({
    valid: false,
    steps: [],
    hints: [],
    allCompleted: false,
  });

  const removeDuplicateFields = (fields = []) => {
    const uniqueMap = new Map();
    for (const field of fields) {
      if (!uniqueMap.has(field.name)) {
        uniqueMap.set(field.name, field);
      }
    }
    return [...uniqueMap.values()];
  };

  useEffect(() => {
    const formFields =
      currentProcessingFile?.document?.tempFields?.formFields || [];
    const visibleSignerIndexes = getSignersWithFields(formFields);
    const hasSigner1 = visibleSignerIndexes.includes(1);
    const hasSigner2 = visibleSignerIndexes.includes(2);
    const hasSigner3 = visibleSignerIndexes.includes(3);

    setShowSigner1(hasSigner1);
    setShowSigner2(hasSigner2);
    setShowSigner3(hasSigner3);
  }, [currentProcessingFile]);

  useEffect(() => {
    if (lockErrorText) {
      setWarnLock(false);
      const timeout = setTimeout(() => {
        setLockErrorText("");
      }, 5000);

      return () => clearTimeout(timeout);
    }
  }, [lockErrorText]);

  useEffect(() => {
    if (warnLock) {
      setWarnLock(false);
    }
  }, [currentProcessingFile, selectedPackage, activeStep, warnLock]);

  const generateRandomName = () => {
    const timestamp = Date.now().toString(); // Get current timestamp as string
    const lastFour = timestamp.slice(-4); // Extract last 4 digits
    return `document_package_form_${lastFour}`;
  };

  useEffect(() => {
    const pkg = userPackages.find(
      (p) => p.packageID === selectedPackage?.packageID
    );

    const doc = pkg?.documents?.find(
      (d) => d.formID === selectedPackageForm?.formID
    );

    const docActions = doc?.actions || {
      notarize: false,
      sign: false,
      record: false,
    };

    setOptions(docActions);
  }, [userPackages, selectedPackage, selectedPackageForm]);

  useEffect(() => {
    if (
      selectedPackageForm?.formID !== currentProcessingFile?.document?.formID &&
      scrollInputs.current
    ) {
      scrollInputs.current.scrollTo({ top: 0, behavior: "smooth" });
    }
  }, [selectedPackageForm?.formID, currentProcessingFile?.document?.formID]);

  const sanitizeFilename = (input) => {
    return input
      .replace(/\s+/g, "_") // ✅ Replace spaces with underscores immediately
      .replace(/[^a-zA-Z0-9._-]/g, "") // ✅ Keep only safe characters
      .replace(/^\.+|\.+$/g, "") // ✅ Remove leading/trailing dots
      .replace(/_{2,}/g, "_") // ✅ Collapse multiple underscores
      .replace(/-{2,}/g, "-"); // ✅ Collapse multiple dashes
  };

  const handleNext = () => {
    if (activeStep === steps?.length - 1) {
      setPackagePanelOpen(false);
      setAddingNewPackage(false);
    }

    setActiveStep((prevStep) => prevStep + 1);
  };
  const handleBack = () => {
    if (activeStep === 0) {
      setAddingNewPackage(false);
    } else {
      setActiveStep((prevStep) => prevStep - 1);
    }
  };

  const handleOptionChange = (event) => {
    const { name, checked } = event.target;

    const updates = [];

    // Always update the clicked option
    updates.push({ name, checked });

    // If selecting 'sign', disable 'notarize' and 'record'
    if (name === "sign" && checked) {
      updates.push({ name: "notarize", checked: false });
      updates.push({ name: "record", checked: false });
    }

    // If selecting 'record' or 'notarize', disable 'sign'
    if ((name === "record" || name === "notarize") && checked) {
      updates.push({ name: "sign", checked: false });
    }

    updates.forEach((opt) =>
      dispatch(
        updateProcessOptions(
          selectedPackage?.packageID,
          selectedPackageForm?.formID,
          opt
        )
      )
    );

    setCurrentProcessingFile((prev) => ({
      ...prev,
      document: {
        ...prev.document,
        actions: {
          ...prev.document.actions,
          ...Object.fromEntries(updates.map((u) => [u.name, u.checked])),
        },
      },
    }));
  };

  useEffect(() => {
    const runValidation = async () => {
      const result = await getLockValidationTodos({
        currentProcessingFile,
        selectedPackageForm,
        showSigner1,
        showSigner2,
        showSigner3,
      });

      setLockChecklist(result);
    };

    runValidation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options, selectedPackageForm, currentProcessingFile]);

  const handleLockUnlock = async () => {
    try {
      setLockLoading(true);
      if (!lockChecklist?.allCompleted) {
        throw new Error("Validation failed");
      }
      const successLocked = await dispatch(
        lockDocument(
          // selectedPackage?.packageID,
          selectedPackage,
          selectedPackageForm?.formID,
          currentProcessingFile?.document
        )
      );

      if (!successLocked?.success) {
        throw new Error("Validation failed");
      }

      const locked = successLocked?.locked;

      await setCurrentProcessingFile((prevFile) => ({
        ...prevFile,
        document: {
          ...prevFile.document,
          locked,
        },
      }));
    } catch (e) {
      if (e.message === "Validation failed") return;
      console.error("Unexpected Lock Error:", e);
    } finally {
      setLockLoading(false);
    }
  };

  const handleDeleteDocument = async () => {
    try {
      let formActive = false;
      let packageActive = false;
      let clearSelected = false;
      let removingLast = false;

      if (selectedPackage?.documents?.length === 1) {
        removingLast = true;
      }

      if (selectedPackage?.documents?.length <= 0 && currentFormsActive) {
        formActive = false;
        packageActive = true;
        clearSelected = true;
      }

      if (
        formActive &&
        selectedPackage &&
        selectedPackage?.documents?.length === 0
      ) {
        return;
      }
      if (
        packageActive &&
        Array.isArray(selectedPackage?.documents) &&
        selectedPackage?.documents?.length > 0
      ) {
        return;
      }

      setDeleteFormLoading(true);
      const resp = await dispatch(
        deleteUserPackageForm(
          selectedPackage?.packageID,
          selectedPackageForm?.formID,
          packageActive
        )
      );

      if (resp === true) {
        if (clearSelected || removingLast) {
          setCurrentProcessingFile(currentProcessingFileBase);
          dispatch(updateSelectedPackageObject(null));
          dispatch(updateSelectedPackageFormObject(null));

          setSavedPackagesActive(true);
          setCurrentFormsActive(false);
        } else if (packageActive) {
          setCurrentProcessingFile(currentProcessingFileBase);

          if (userPackages?.length > 0) {
            dispatch(updateSelectedPackageObject(userPackages[0]));

            if (userPackages[0]?.documents?.length > 0) {
              dispatch(
                updateSelectedPackageFormObject(userPackages[0]?.documents[0])
              );
            }
          } else {
            dispatch(updateSelectedPackageObject(null));
          }
        } else {
          const updatedPackageData = {
            ...selectedPackage,
            documents: selectedPackage.documents.filter(
              (doc) => doc.formID !== selectedPackageForm?.formID
            ),
          };

          const updatedSelectedForm =
            updatedPackageData.documents?.length > 0
              ? updatedPackageData?.documents[0]
              : currentProcessingFileBase?.document;

          await setCurrentProcessingFile((prevFile) => ({
            ...prevFile,
            document: updatedSelectedForm,
          }));
          dispatch(updateSelectedPackageObject(updatedPackageData));
          dispatch(updateSelectedPackageFormObject(updatedSelectedForm));
        }
        setDeleteFormModalOpen(false);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setDeleteFormLoading(false);
    }
  };

  return (
    <>
      {/* Fixed Header with Stepper */}
      <Box
        sx={{
          position: "sticky",
          top: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          borderBottom: "3px solid #e0e0e0",
        }}
      >
        <Typography mb={0} align="center" variant="h4" color="primary">
          {selectedPackage?.packageName}
        </Typography>
        <Box
          onClick={() => {
            if (updatingFileName === selectedPackageForm?.formID) {
              return;
            }
            setUpdatingFileName(selectedPackageForm?.formID);
            setNewFileName(selectedPackageForm?.cleaned_file_name);
          }}
        >
          {updatingFileName === selectedPackageForm?.formID ? (
            <input
              type="text"
              value={newFileName}
              onChange={(e) => {
                const inputElement = e.target;
                const cursorPosition = inputElement.selectionStart;

                const sanitized = sanitizeFilename(e.target.value);
                setNewFileName(sanitized);

                requestAnimationFrame(() => {
                  inputElement.setSelectionRange(
                    cursorPosition,
                    cursorPosition
                  );
                });
              }}
              autoFocus
              onBlur={() => {
                if (
                  newFileName.trim().replace(/^[_\s]+|[_\s]+$/g, "") !==
                  selectedPackageForm?.cleaned_file_name
                ) {
                  let randomName = null;

                  if (!newFileName.trim().replace(/^[_\s]+|[_\s]+$/g, "")) {
                    randomName = generateRandomName();
                  } else {
                    randomName = newFileName
                      .trim()
                      .replace(/^[_\s]+|[_\s]+$/g, "");
                  }

                  dispatch(
                    updateNewFileName(
                      selectedPackage?.packageID,
                      selectedPackageForm?.formID,
                      randomName
                    )
                  );

                  setCurrentProcessingFile((prevFile) => ({
                    ...prevFile,
                    document: {
                      ...prevFile.document,
                      cleaned_file_name: randomName,
                    },
                  }));
                  const updatedPackage = {
                    ...selectedPackage,
                    documents: selectedPackage.documents.map((item) =>
                      item.formID === selectedPackageForm.formID
                        ? {
                            ...item,
                            cleaned_file_name: randomName,
                          }
                        : item
                    ),
                  };

                  const updatedForm = {
                    ...selectedPackageForm,
                    cleaned_file_name: randomName,
                  };

                  dispatch(updateSelectedPackageObject(updatedPackage));
                  dispatch(updateSelectedPackageFormObject(updatedForm));
                  setNewFileName(null);
                }
                setUpdatingFileName(null);
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setUpdatingFileName(null);
                }
              }}
              style={{
                fontFamily: `"Roboto", "Helvetica", "Arial", sans-serif`,
                fontSize: "0.875rem",
                fontWeight: "bold",
                lineHeight: "1.5",
                color: "#004976",
                border: "none",
                background: "transparent",
                textAlign: "center",
                width: "100%",
                padding: "0",
                marginBottom: "9px",
                outline: "none",
                cursor: "text",
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                MsUserSelect: "none",
              }}
            />
          ) : (
            <Typography
              mb={1}
              align="center"
              variant="body2"
              fontWeight="bold"
              color="primary"
              sx={{
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                MsUserSelect: "none",
                cursor: "pointer",
              }}
            >
              {selectedPackageForm?.cleaned_file_name}
            </Typography>
          )}
        </Box>

        <Box sx={{ position: "absolute", right: 0, top: 0 }}>
          <IconButton
            disabled={
              selectedPackageForm?.locked === true ||
              currentProcessingFile?.document?.locked === true
            }
            size="small"
            onClick={() => {
              setPackagePanelOpen(false);
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Box>
          <Typography variant="h5" color="primary" align="center">
            {activeStep === 0 ? "Document Overview" : "Add/Edit Signer Details"}
          </Typography>
          <Typography
            variant="body2"
            fontWeight="bold"
            color="primary"
            align="center"
          >
            {activeStep === 1
              ? "These details will be applied to all documents in this package"
              : "To proceed, select the desired action and confirm by locking the document"}
          </Typography>
        </Box>
      </Box>
      {/* Scrollable Content */}
      <Box
        ref={scrollInputs}
        sx={{
          position: "relative",
          flexGrow: 1,
          overflowY:
            selectedPackageForm?.formID !==
              currentProcessingFile?.document?.formID ||
            selectedPackageForm?.locked
              ? "hidden"
              : "auto",
          px: 2,
          py: 2,
        }}
      >
        {selectedPackageForm?.formID !==
          currentProcessingFile?.document?.formID ||
        selectedPackageForm?.locked ? (
          <Box
            sx={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              backgroundColor: "rgba(255, 255, 255, 0.9)",
              zIndex: 20,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "center",
            }}
          >
            {!savedPackagesActive &&
            !selectedPackageForm?.locked &&
            selectedPackage?.documents?.length > 0 ? (
              <>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {loadDocumentLoading
                    ? "Loading Document. Please Wait..."
                    : "This form is not loaded yet"}
                </Typography>
                <LoadingButton
                  disabled={
                    (selectedPackage &&
                      selectedPackage?.documents?.length === 0) ||
                    (selectedPackageForm && !selectedPackageForm?.formID) ||
                    !selectedPackageForm
                  }
                  loading={loadDocumentLoading}
                  variant="contained"
                  color="primary"
                  startIcon={<InsertDriveFileIcon />}
                  onClick={() =>
                    dispatch(
                      handleLoadDocument(
                        setLoadDocumentLoading,
                        setCurrentProcessingFile
                      )
                    )
                  }
                >
                  Load Document
                </LoadingButton>
              </>
            ) : selectedPackage?.documents?.length <= 0 ? (
              <>
                <Typography variant="h6">
                  This Package Has No Documents
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  You can either upload a document to get started or delete this
                  empty package
                </Typography>
                <LoadingButton
                  disabled={deleteFormLoading}
                  loading={deleteFormLoading}
                  variant="contained"
                  color="error"
                  startIcon={<DeleteIcon />}
                  onClick={() => {
                    setDeleteFormModalOpen(true);
                  }}
                >
                  Delete Empty Package
                </LoadingButton>
              </>
            ) : (
              selectedPackageForm?.locked && (
                <>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      gap: 0.5,
                    }}
                  >
                    <Typography color="primary" variant="h5">
                      This document is created
                    </Typography>
                    <LockIcon color="primary" sx={{ fontSize: 30 }} />
                  </Box>
                  <Box mt={1}>
                    <LoadingButton
                      loading={loadDocumentLoading}
                      onClick={() => {
                        dispatch(
                          unlockDocument(
                            selectedPackage?.packageID,
                            selectedPackageForm?.formID
                          )
                        );
                        dispatch(
                          handleLoadDocument(
                            setLoadDocumentLoading,
                            setCurrentProcessingFile
                          )
                        );
                        setCurrentProcessingFile((prevFile) => {
                          const dedupedFields = removeDuplicateFields(
                            prevFile?.document?.tempFields?.formFields || []
                          );

                          // ✅ Update current file
                          const updatedFile = {
                            ...prevFile,
                            document: {
                              ...prevFile.document,
                              locked: false,
                              tempFields: {
                                ...prevFile.document.tempFields,
                                formFields: dedupedFields,
                              },
                            },
                          };
                          // ✅ Update redux if needed
                          dispatch(
                            updateTempFields({
                              packageID: selectedPackage?.packageID,
                              formID: selectedPackageForm?.formID,
                              formFields: dedupedFields,
                            })
                          );

                          return updatedFile;
                        });
                      }}
                      variant="contained"
                      endIcon={<EditIcon />}
                    >
                      Edit Document
                    </LoadingButton>
                  </Box>
                </>
              )
            )}
          </Box>
        ) : (
          <></>
        )}

        {activeStep === 0 && (
          <>
            <Card
              sx={{
                mb: 3,
                boxShadow: "3px 5px 6px 4px rgba(0, 0, 0, 0.2) !important",
              }}
            >
              <CardContent sx={{ pt: 3, pb: "1rem !important" }}>
                <Typography align="center" color="primary" variant="h6">
                  Select What You Want to Do with This Form
                </Typography>
                <Typography align="center" color="primary" variant="body2">
                  You can validate once all fields has been marked on the
                  document
                </Typography>
                <Grid container spacing={2}>
                  {["notarize", "sign", "record"]?.map((option) => (
                    <Grid
                      align="center"
                      sx={{ mx: "auto" }}
                      size={{ xs: 3 }}
                      key={option}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              options?.[option] ?? false // ✅ Ensure `false` if undefined
                            }
                            onChange={handleOptionChange}
                            name={option}
                            disabled={
                              selectedPackageForm?.locked ||
                              (option === "sign" &&
                                (options?.record || options?.notarize)) ||
                              ((option === "record" || option === "notarize") &&
                                options?.sign)
                            }
                          />
                        }
                        label={option.charAt(0).toUpperCase() + option.slice(1)}
                      />
                    </Grid>
                  ))}
                </Grid>
                {!selectedPackageForm?.locked && (
                  <Box
                    sx={{
                      flexShrink: 0,
                      mx: "auto",
                      width: warnLock ? "100%" : undefined,
                      display: warnLock ? "flex" : undefined,
                      justifyContent: warnLock ? "center" : undefined,
                    }}
                  >
                    <Box
                      gap={2}
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        mx: "auto",
                        mt: 1,
                      }}
                    >
                      {!warnLock ? (
                        <LoadingButton
                          disabled={!lockChecklist?.allCompleted}
                          loading={lockLoading}
                          variant={
                            selectedPackageForm?.locked
                              ? "contained"
                              : "outlined"
                          }
                          size="small"
                          color={
                            selectedPackageForm?.locked
                              ? "secondary"
                              : "primary"
                          }
                          onClick={() => {
                            setLockErrorText("");
                            setWarnLock(true);
                          }}
                          startIcon={
                            selectedPackageForm?.locked ? (
                              <LockIcon />
                            ) : (
                              <LockOpenIcon />
                            )
                          }
                        >
                          Validate & Lock
                        </LoadingButton>
                      ) : (
                        <Box
                          sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            justifyContent: "center",
                            textAlign: "center", // helps with text alignment too
                            width: "100%",

                            mx: "auto",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="primary"
                            fontWeight="bold"
                          >
                            Have you reviewed all the hints and want to Record
                            this document?
                          </Typography>
                          <Box sx={{ display: "flex" }} mt={1} gap={2}>
                            <Button
                              onClick={() => {
                                setWarnLock(false);
                              }}
                              size="small"
                              variant="outlined"
                              disabled={lockLoading}
                              startIcon={
                                selectedPackageForm?.locked ? (
                                  <LockIcon />
                                ) : (
                                  <LockOpenIcon />
                                )
                              }
                              color="error"
                            >
                              Cancel
                            </Button>
                            <LoadingButton
                              loading={lockLoading}
                              disabled={lockLoading}
                              variant={"contained"}
                              size="small"
                              color={
                                selectedPackageForm?.locked
                                  ? "secondary"
                                  : "primary"
                              }
                              onClick={() => {
                                handleLockUnlock(true);
                              }}
                              startIcon={
                                selectedPackageForm?.locked ? (
                                  <LockIcon />
                                ) : (
                                  <LockOpenIcon />
                                )
                              }
                            >
                              Proceed
                            </LoadingButton>
                          </Box>
                        </Box>
                      )}
                    </Box>
                  </Box>
                )}
                <Box
                  sx={{
                    border: "1px solid #e0e0e0",
                    borderRadius: 1,
                    mt: 1.5,
                    p: 1,
                  }}
                >
                  {lockChecklist.steps.map((step) => (
                    <Box
                      key={step.msg}
                      sx={{ display: "flex", alignItems: "center", mb: 1 }}
                    >
                      {step.completed ? (
                        <CheckIcon color="success" sx={{ mr: 1 }} />
                      ) : (
                        <CloseIcon color="error" sx={{ mr: 1 }} />
                      )}
                      <Typography>{step.msg}</Typography>
                    </Box>
                  ))}

                  {lockChecklist.hints.length > 0 && (
                    <>
                      <Divider sx={{ my: 2 }} />
                      <Typography
                        fontWeight="bold"
                        variant="subtitle2"
                        gutterBottom
                      >
                        Hints: Please Make Sure:
                      </Typography>
                      {lockChecklist.hints.map((hint) => (
                        <Typography
                          key={hint.msg}
                          variant="body2"
                          sx={{ ml: 3, mb: 0.5 }}
                        >
                          • {hint.msg}
                        </Typography>
                      ))}
                    </>
                  )}
                </Box>
                <Box
                  sx={{
                    mt: 1,

                    flexGrow: 1,
                    overflow: "hidden",
                  }}
                >
                  <Typography
                    align="center"
                    fontWeight="bold"
                    variant="body2"
                    color="error"
                    sx={{ wordWrap: "break-word" }}
                  >
                    {lockErrorText}
                  </Typography>
                </Box>
              </CardContent>
            </Card>
            {!savedPackagesActive &&
              selectedPackageForm?.img_urls?.map((img, index) => (
                <Magnifier
                  key={index}
                  src={img}
                  lensSize={400}
                  zoomFactor={0.2}
                />
              ))}
          </>
        )}

        {activeStep === 1 && (
          <>
            {!savedPackagesActive && (
              <RenderedPackageFormFields
                setCurrentProcessingFile={setCurrentProcessingFile}
              />
            )}
          </>
        )}
      </Box>

      <Box
        sx={{
          borderTop: "3px solid #e0e0e0",
          borderBottom: "3px solid #e0e0e0",
          borderRadius: "1rem 1rem 0 0",
          boxShadow:
            "0px -6px 12px rgba(0,0,0,0.08), 0px 6px 12px rgba(0,0,0,0.4)", // only vertical shadows
          position: "sticky",
          bottom: 0,
          zIndex: 2,
          bgcolor: "white",
          p: 2,
          display: "flex",
          justifyContent: "center",
        }}
      >
        {/* Center Button */}
        {selectedPackage?.documents?.length > 0 &&
          selectedPackage?.documents?.every((doc) => doc.locked === true) && (
            <Box
              sx={{
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)",
              }}
            >
              <Button
                size="medium"
                variant="contained"
                color="success"
                startIcon={<FaPlay />}
                onClick={() => {
                  setOpenProcessModal(true);
                }}
                sx={{ width: "20rem" }}
              >
                Start Document Processing
              </Button>
            </Box>
          )}
        <Box sx={{ position: "absolute", left: 0 }}>
          <IconButton
            onClick={() => {
              if (
                currentFormsActive &&
                selectedPackage &&
                selectedPackage?.documents?.length === 0
              ) {
                return;
              }
              setDeleteFormModalOpen(true);
            }}
            disabled={
              (savedPackagesActive &&
                Array.isArray(selectedPackage?.documents) &&
                selectedPackage?.documents?.length > 0) ||
              (currentFormsActive &&
                selectedPackage &&
                selectedPackage?.documents?.length === 0) ||
              selectedPackageForm?.locked
            }
            sx={{
              visibility:
                currentFormsActive &&
                (selectedPackageForm?.formID !==
                  currentProcessingFile?.document?.formID ||
                  selectedPackageForm?.locked)
                  ? "hidden"
                  : "visible",
              "&:hover": {
                backgroundColor: "rgba(255, 0, 0, 0.1)", // ✅ Light red hover effect
              },
            }}
          >
            <DeleteIcon
              sx={{
                color:
                  (savedPackagesActive &&
                    Array.isArray(selectedPackage?.documents) &&
                    selectedPackage?.documents?.length > 0) ||
                  (currentFormsActive &&
                    selectedPackage &&
                    selectedPackage?.documents?.length === 0)
                    ? undefined
                    : "red",
              }}
            />
          </IconButton>
        </Box>

        <Button
          disabled={activeStep === 0}
          size="small"
          sx={{
            mr: 1,
            visibility:
              savedPackagesActive ||
              selectedPackageForm?.formID !==
                currentProcessingFile?.document?.formID ||
              selectedPackageForm?.locked
                ? "hidden"
                : "visible",
          }}
          onClick={handleBack}
          variant="outlined"
          startIcon={<ArrowBackIcon />}
        >
          Overview
        </Button>

        <Button
          disabled={
            activeStep === steps?.length - 1 ||
            !selectedPackageForm ||
            !selectedPackageForm?.formID
          }
          size="small"
          variant="outlined"
          sx={{
            ml: 1,
            mr: 2,
            visibility:
              savedPackagesActive ||
              selectedPackageForm?.formID !==
                currentProcessingFile?.document?.formID ||
              selectedPackageForm?.locked
                ? "hidden"
                : "visible",
          }}
          onClick={handleNext}
          endIcon={<ArrowForwardIcon />}
        >
          Signer Info
        </Button>

        <Box sx={{ position: "absolute", right: 10 }}>
          <Button
            onClick={() => {
              setPackagePanelOpen(false);
            }}
            startIcon={<EditIcon />}
            size="small"
            color="warning"
            variant="contained"
            sx={{
              visibility:
                savedPackagesActive ||
                selectedPackageForm?.formID !==
                  currentProcessingFile?.document?.formID ||
                selectedPackageForm?.locked
                  ? "hidden"
                  : "visible",
            }}
          >
            Open Form Editor
          </Button>
        </Box>

        <DeleteFormModal
          open={deleteFormModalOpen}
          onClose={() => {
            if (!deleteFormLoading) {
              setDeleteFormModalOpen(false);
            }
          }}
          onConfirm={handleDeleteDocument}
          documentName={selectedPackageForm?.document_name}
          packageName={selectedPackage?.packageName}
          deleteFormLoading={deleteFormLoading}
          savedPackagesActive={
            selectedPackage?.documents?.length <= 0 ? true : savedPackagesActive
          }
        />
      </Box>
    </>
  );
};

export default SelectedPackage;
